import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Heart, Languages, Mail, Menu, Phone, PlusCircle, Search, X } from 'lucide-react';
import { useSaved } from '@/lib/saved';
import { Facebook, Instagram, Youtube } from '@/components/common/SocialIcons';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon } from '@/components/ui';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useSite } from '@/lib/site';
import type { MenuItem } from '@/lib/types';
import { cn, isExternal, isSafeUrl } from '@/lib/utils';

function SmartLink({ item, className, onClick, children }: { item: MenuItem; className?: string; onClick?: () => void; children: React.ReactNode }) {
  if (!isSafeUrl(item.url)) return null;
  if (isExternal(item.url) || item.newTab) {
    return <a href={item.url} target={item.newTab ? '_blank' : undefined} rel="noopener noreferrer" className={className} onClick={onClick}>{children}</a>;
  }
  return <NavLink to={item.url} end={item.url === '/'} className={({ isActive }) => cn(className, isActive && 'is-active')} onClick={onClick}>{children}</NavLink>;
}

const visibleOn = (items: MenuItem[] | undefined, device: 'desktop' | 'mobile') => (items ?? []).filter((i) => i.enabled !== false && (i.visible?.[device] ?? true));

export function Brand({ light }: { light?: boolean }) {
  const site = useSite();
  const en = site.data?.siteName?.en || 'Mana Yelamanchili';
  const [first, ...rest] = en.split(' ');
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2" aria-label={`${en} home`}>
      {site.data?.logo?.url ? <img src={site.data.logo.url} alt="" className="size-11 rounded-xl object-contain" /> : <img src="/favicon.svg" alt="" className="size-11" />}
      <span className="leading-none">
        <span className={cn('block font-display text-xl font-bold tracking-tight', light ? 'text-white' : 'text-brand-950')}>
          {first} <span className="text-forest-500">{rest.join(' ')}</span>
        </span>
        <span className={cn('mt-1 block font-telugu text-xs font-semibold', light ? 'text-white/70' : 'text-ink-muted')}>{site.data?.siteName?.te || 'మన యలమంచిలి'}</span>
      </span>
    </Link>
  );
}

function LangToggle({ className }: { className?: string }) {
  const { lang, setLang, t } = useI18n();
  return (
    <Button variant="ghost" size="sm" className={className} onClick={() => setLang(lang === 'en' ? 'te' : 'en')} aria-label={lang === 'en' ? 'Switch to Telugu' : 'Switch to English'}>
      <Languages /> <span className={cn('2xl:inline', className?.includes('w-full') ? 'inline' : 'hidden', lang === 'en' && 'font-telugu')}>{t('language')}</span>
    </Button>
  );
}

function SavedLink() {
  const n = useSaved().length;
  return (
    <Link to="/saved" aria-label={`Saved places (${n})`} className="relative flex size-10 items-center justify-center rounded-xl text-ink-soft hover:bg-surface-sunken hover:text-rose-500">
      <Heart className="size-5" />
      {n > 0 && <span className="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{n}</span>}
    </Link>
  );
}

function Header() {
  const site = useSite();
  const { L, t } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  const items = visibleOn(site.data?.menus?.header, 'desktop');
  const mobileItems = visibleOn(site.data?.menus?.header, 'mobile');
  const linkCls = 'whitespace-nowrap rounded-full px-2.5 py-1.5 text-[13px] font-medium text-ink-soft transition hover:bg-brand-50 hover:text-brand-800 [&.is-active]:bg-brand-600 [&.is-active]:text-white [&.is-active]:shadow-sm';
  return (
    <header className="sticky top-0 z-40 border-b border-line/70 bg-white/95 shadow-[0_1px_12px_rgba(17,30,74,0.05)] backdrop-blur supports-[backdrop-filter]:bg-white/85">
      {site.data?.announcement?.enabled && L(site.data.announcement.text) && (
        <div className="bg-brand-900 px-4 py-2 text-center text-sm text-white">
          {site.data.announcement.url && isSafeUrl(site.data.announcement.url) ? <a href={site.data.announcement.url} className="underline underline-offset-2">{L(site.data.announcement.text)}</a> : L(site.data.announcement.text)}
        </div>
      )}
      <div className="container flex h-[68px] items-center gap-3">
        <Brand />
        <nav aria-label="Main" className="ml-2 hidden flex-1 items-center gap-0.5 xl:flex">
          {items.map((item) =>
            item.children?.filter((c) => c.enabled !== false).length ? (
              <DropdownMenu.Root key={item.id}>
                <DropdownMenu.Trigger className={cn(linkCls, 'inline-flex items-center gap-1 data-[state=open]:bg-brand-50')}>
                  {L(item.label)} <ChevronDown className="size-3.5" />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="start" sideOffset={6} className="z-50 min-w-[14rem] rounded-xl border border-line bg-white p-1.5 shadow-lift animate-fade-in">
                    {item.children.filter((c) => c.enabled !== false && (c.visible?.desktop ?? true)).map((c) => (
                      <DropdownMenu.Item key={c.id} asChild>
                        <SmartLink item={c} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-brand-50">
                          {c.icon && <Icon name={c.icon} className="size-4 text-brand-700" />} {L(c.label)}
                        </SmartLink>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            ) : (
              <SmartLink key={item.id} item={item} className={linkCls}>{L(item.label)}</SmartLink>
            ),
          )}
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <form role="search" onSubmit={(e) => { e.preventDefault(); const q = new FormData(e.currentTarget).get('q'); navigate(q ? `/search?q=${encodeURIComponent(String(q))}` : '/search'); }} className="relative hidden 2xl:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
            <input name="q" aria-label={t('search')} placeholder={`${t('search')}…`} className="h-9 w-52 rounded-full border border-line bg-surface-muted pl-9 pr-3 text-sm placeholder:text-ink-muted focus:border-brand-400 focus:bg-white focus:outline-none" />
          </form>
          <Button variant="ghost" size="icon" className="2xl:hidden" aria-label={t('search')} onClick={() => navigate('/search')}><Search className="size-5" /></Button>
          <SavedLink />
          <LangToggle className="hidden sm:inline-flex" />
          <Button size="sm" className="hidden rounded-full px-4 md:inline-flex" onClick={() => navigate('/submit')}><PlusCircle /> {t('submitListing')}</Button>
          <Button variant="ghost" size="icon" className="xl:hidden" aria-label={t('menu')} aria-expanded={open} onClick={() => setOpen(true)}><Menu className="size-5" /></Button>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 xl:hidden" role="dialog" aria-modal="true" aria-label={t('menu')}>
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 right-0 flex w-[min(22rem,88vw)] flex-col bg-white shadow-lift animate-fade-in">
            <div className="flex h-[68px] items-center justify-between border-b border-line px-4">
              <Brand />
              <Button variant="ghost" size="icon" aria-label={t('close')} onClick={() => setOpen(false)}><X className="size-5" /></Button>
            </div>
            <nav aria-label="Mobile" className="flex-1 overflow-y-auto p-3">
              {mobileItems.map((item) => (
                <div key={item.id} className="mb-1">
                  <SmartLink item={item} className="flex items-center gap-3 rounded-xl px-3 py-3 text-base font-medium text-ink hover:bg-brand-50 [&.is-active]:bg-brand-50 [&.is-active]:text-brand-900">
                    {item.icon && <Icon name={item.icon} className="size-5 text-brand-700" />} {L(item.label)}
                  </SmartLink>
                  {item.children?.filter((c) => c.enabled !== false && (c.visible?.mobile ?? true)).map((c) => (
                    <SmartLink key={c.id} item={c} className="ml-8 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-ink-soft hover:bg-brand-50">{L(c.label)}</SmartLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className="space-y-2 border-t border-line p-4">
              <Button className="w-full" onClick={() => navigate('/submit')}><PlusCircle /> {t('submitListing')}</Button>
              <LangToggle className="w-full justify-center" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function BottomNav() {
  const site = useSite();
  const { L } = useI18n();
  const items = visibleOn(site.data?.menus?.mobile_bottom, 'mobile').slice(0, 5);
  if (!site.data?.features?.mobileBottomNav || !items.length) return null;
  return (
    <nav aria-label="Bottom navigation" className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((item) => (
          <li key={item.id}>
            <SmartLink item={item} className="flex flex-col items-center gap-0.5 px-1 py-2 text-[11px] font-medium text-ink-muted [&.is-active]:text-brand-800">
              <Icon name={item.icon || 'Link'} className="size-5" />
              <span className="max-w-full truncate">{L(item.label)}</span>
            </SmartLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Skyline() {
  // Line-art town skyline with temple tower, clock tower, houses and birds (decorative).
  return (
    <svg viewBox="0 0 520 90" className="h-20 w-full max-w-md text-white/25" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden>
      <path d="M0 86h520" />
      <path d="M20 86V60h22v26M31 60V52M58 86V46l14-10 14 10v40M66 86V70h12v16" />
      <path d="M110 86V64h40v22M118 64l12-12 12 12M122 72h6v6h-6zM134 72h6v6h-6z" />
      <path d="M176 86V48h24v38M180 48l8-10 8 10M188 38v-6M184 58h8M184 68h8" />
      <path d="M226 86V70h68v16M232 70V58h56v12M238 58V48h44v10M244 48V40h32v8M250 40v-8h20v8M256 32l4-8 4 8" />
      <path d="M318 86V56h30v30M322 56l11-10 11 10M326 66h6v8h-6zM336 66h6v8h-6z" />
      <path d="M368 86V40h18v46M372 40h10M377 40V28M371 50h8M371 60h8M371 70h8" />
      <path d="M404 86V66h46v20M410 66l17-14 17 14M420 76h14" />
      <path d="M466 86V52h34v34M472 52l11-12 11 12M480 62h6v10h-6z" />
      <path d="M430 18q6-6 12 0q6-6 12 0M462 10q4-4 8 0q4-4 8 0M396 26q4-4 8 0q4-4 8 0" />
    </svg>
  );
}

function Footer() {
  const site = useSite();
  const { L, lang } = useI18n();
  const s = site.data;
  const col = (key: string, title: { en: string; te: string }) => {
    const items = visibleOn(s?.menus?.[key], 'desktop');
    if (!items.length) return null;
    return (
      <div>
        <h3 className="mb-3 text-sm font-semibold text-white">{title[lang]}</h3>
        <ul className="space-y-2 text-sm">
          {items.map((i) => <li key={i.id}><SmartLink item={i} className="text-white/65 hover:text-white">{L(i.label)}</SmartLink></li>)}
        </ul>
      </div>
    );
  };
  const social = [
    { href: s?.social?.facebook, Icon: Facebook, label: 'Facebook', cls: 'bg-[#1877f2]' },
    { href: s?.social?.instagram, Icon: Instagram, label: 'Instagram', cls: 'bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af]' },
    { href: s?.social?.youtube, Icon: Youtube, label: 'YouTube', cls: 'bg-[#ff0000]' },
  ].filter((x) => x.href && isSafeUrl(x.href));
  return (
    <footer className="mt-16 bg-brand-950 pb-20 text-white md:pb-0">
      <div className="container grid gap-10 py-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Brand light />
          <p className="mt-3 font-script text-2xl text-white/90">{L(s?.tagline) || 'Discover Our Town. Celebrate Our Heritage.'}</p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">{L(s?.footer?.about)}</p>
          <div className="mt-5 space-y-2 text-sm text-white/75">
            {s?.contact?.email && <a href={`mailto:${s.contact.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="size-4" />{s.contact.email}</a>}
            {s?.contact?.phone && <a href={`tel:${s.contact.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="size-4" />{s.contact.phone}</a>}
          </div>
          {social.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-sm font-semibold">{lang === 'te' ? 'మమ్మల్ని అనుసరించండి' : 'Follow us'}</p>
              <div className="flex gap-2">
                {social.map(({ href, Icon: I, label, cls }) => <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className={cn('flex size-9 items-center justify-center rounded-full text-white transition hover:scale-105', cls)}><I className="size-4" /></a>)}
              </div>
            </div>
          )}
        </div>
        {col('footer_explore', { en: 'Quick links', te: 'త్వరిత లింకులు' })}
        {col('footer_community', { en: 'Community', te: 'కమ్యూనిటీ' })}
        {col('footer_legal', { en: 'Support & legal', te: 'సహాయం & చట్టపరమైన' })}
      </div>
      <div className="container flex flex-col items-center justify-between gap-4 border-t border-white/10 py-6 md:flex-row">
        <Skyline />
        <p className="text-sm font-medium tracking-wide text-white/80">{lang === 'te' ? 'మన ఊరు · మన ప్రజలు · మన గర్వం' : 'Our Town · Our People · Our Pride'}</p>
      </div>
      <div className="bg-black/20">
        <div className="container flex flex-col gap-2 py-4 text-xs text-white/55 md:flex-row md:items-center md:justify-between">
          <p>{s?.footer?.copyright || '© Mana Yelamanchili'}</p>
          {L(s?.footer?.disclaimer) && <p className="max-w-2xl">{L(s?.footer?.disclaimer)}</p>}
        </div>
      </div>
    </footer>
  );
}

export function SiteLayout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  // Anonymous daily page-view count (shown on the admin dashboard); failures are ignored.
  useEffect(() => { api.post('/api/public/track', { path: pathname }).catch(() => undefined); }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <a href="#main" className="sr-only z-50 rounded bg-white px-3 py-2 focus:not-sr-only focus:fixed focus:left-3 focus:top-3">Skip to content</a>
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <BottomNav />
    </div>
  );
}
